import type { FlightingShape, PopulatedFlight, RegistrationLike } from "./flightPlacementTypes";
import { getEffectiveFlights } from "./getEffectiveFlights";
import { getEmptyPopulatedFlights } from "./getEmptyPopulatedFlights";
import { placeRegistrationsManual } from "./placeRegistrationsManual";
import { placeRegistrationsAuto } from "./placeRegistrationsAuto";

export interface PlaceRegistrationsParams {
    /** Flighting config (divisions, flights, assignment mode). */
    flighting: FlightingShape | undefined;
    /** All registrations to place into flights. */
    registrations: RegistrationLike[];
}

/**
 * Main entry point: computes populated flights from flighting config and registrations.
 *
 * PROCESS:
 * 1. Read assignment mode (manual vs auto) from flighting.props.
 * 2. Resolve effective flights (real flights or one-per-division) via getEffectiveFlights.
 * 3. Build empty populated flights via getEmptyPopulatedFlights.
 * 4. Branch on assignment mode:
 *    - manual: place each registration by division/flight name into the matching flight.
 *    - auto: for each division and its flights, filter registrations by division/flight
 *      criteria (gender, age, handicap) and push into the corresponding flight.
 * 5. Return the populated flights array (ready for setPopulatedFlights in the UI).
 */
export function placeRegistrations(params: PlaceRegistrationsParams): PopulatedFlight[] {
    const { flighting, registrations } = params;

    const divisions = flighting?.divisions ?? [];
    const assignmentMode = (flighting?.props?.assignmentMode as string) || "auto";

    const { effectiveFlights, useDivisionsAsFlights } = getEffectiveFlights(flighting);
    const newPopulatedFlights = getEmptyPopulatedFlights(effectiveFlights);

    if (assignmentMode === "manual") {
        placeRegistrationsManual({
            registrations,
            divisions,
            effectiveFlights,
            populatedFlights: newPopulatedFlights,
            useDivisionsAsFlights,
        });
    } else {
        placeRegistrationsAuto({
            registrations,
            divisions,
            effectiveFlights,
            populatedFlights: newPopulatedFlights,
            assignmentMode,
        });
    }

    return newPopulatedFlights;
}
